import axiosInstance from "./axios";

export const fetchThreadDetails = async (threadId, token) => {
  try {
    const { data } = await axiosInstance.get(`/chat/threads/${threadId}/`, {
      headers: { Authorization: `Token ${token}` },
    });
    return data;
  } catch (err) {
    console.error("Error fetching thread details", err);
    throw err;
  }
};

export const fetchThreadMessages = async (threadId, token) => {
  try {
    const { data } = await axiosInstance.get(`/chat/threads/${threadId}/messages/`, {
      headers: { Authorization: `Token ${token}` },
    });
    return data;
  } catch (err) {
    console.error("Error fetching messages");
    throw err;
  }
};

export const deleteThread = async (threadId, token) => {
  try {
    const { data } = await axiosInstance.get(`/chat/threads/${threadId}/`, {
      headers: { Authorization: `Token ${token}` },
    });

    return data;
  } catch (err) {
    console.error("Error deleting thread");
    throw err;
  }
};

export const addThreadParticipants = async (threadId, participantIds, token) => {
  try {
    const { data } = await axiosInstance.post(
      `/chat/threads/${threadId}/`,
      { user_ids: participantIds },
      { headers: { Authorization: `Token ${token}` } }
    );

    return data;
  } catch (err) {
    console.error("Error adding participant[s] to chat");
    throw err;
  }
};

export const updateThreadTitle = async (threadId, title) => {
  try {
    const { data } = axiosInstance.patch(
      `/chat/threads/${threadId}/`,
      { title: title }
      // { headers: { Authorization: `Token ${token}` } }
    );
    return data;
  } catch (err) {
    console.error("Error updating title");
    throw err;
  }
};

// Uploads an image for use in a chat message. Returns the created
// MessageImage asset ({ id, image, image_url, created_at }).
export const uploadMessageImage = async (file, token) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await axiosInstance.post(
      `/chat/threads/messages/images/upload/`,
      formData,
      {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  } catch (err) {
    console.error("Error uploading message image", err);
    throw err;
  }
};

// Finalizes a message video upload after the raw file has been PUT to S3
// (see getPresign/uploadToS3 in PostAPI.js, reused as-is for the S3 leg).
// Creates a MessageVideo row server-side and kicks off async transcoding.
export const finalizeMessageVideo = async ({ key, file }, token) => {
  try {
    const { data } = await axiosInstance.post(
      `/uploads/message-video/finalize/`,
      {
        key,
        original_filename: file.name,
        mime: file.type,
      },
      {
        headers: { Authorization: `Token ${token}` },
      }
    );
    return data; // { id, status: "pending" }
  } catch (err) {
    console.error("Error finalizing message video upload", err);
    throw err;
  }
};

// Reads current processing status for a MessageVideo asset.
export const fetchMessageVideoStatus = async (videoId, token) => {
  try {
    const { data } = await axiosInstance.get(
      `/chat/threads/messages-video/status/${videoId}/`,
      {
        headers: { Authorization: `Token ${token}` },
      }
    );
    return data; // { id, status, video_url, ... }
  } catch (err) {
    console.error("Error fetching message video status", err);
    throw err;
  }
};

// Polls message video status until processing finishes (or errors/times out).
export const pollMessageVideoUntilReady = async (
  videoId,
  token,
  { intervalMs = 2000, timeoutMs = 5 * 60 * 1000 } = {}
) => {
  const start = Date.now();
  while (true) {
    const data = await fetchMessageVideoStatus(videoId, token);

    if (data.status === "done") return data;
    if (data.status === "error") throw new Error("Video processing failed.");
    if (Date.now() - start > timeoutMs) {
      throw new Error("Timed out waiting for video to finish processing.");
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
};

export const markThreadRead = async (threadId, token) => {
  try {
    const { data } = axiosInstance.post(
      `/chat/threads/${threadId}/read/`,
      {},
      {
        headers: { Authorization: `Token ${token}` },
      }
    );
    return data;
  } catch (err) {
    console.error("Error marking thread as read", err);
    throw err;
  }
};
